<script lang="ts">
    import type {Field} from 'node_modules/svelte-forms/types';
    import type {ObjectValueEditorField, ObjectValueEditorLocalizations} from './types';
    import KpValueEditor from '../../../kp-value-editor/KpValueEditor.svelte';
    import {getContext} from 'svelte';
    import {KpValueEditorConfigurationService} from '../../../kp-value-editor/kp-value-editor-configuration.service';
    import {onMount} from 'svelte';
    import type {FormController} from '../../forms/types';
    import type {Observable} from 'rxjs';
    import {tooltip} from '../../../../ui-widgets/tooltip/use.tooltip';
    import {EDITORS_LOCALIZE} from '../../../context-keys';
    import type {ObjectValueEditorLabelsWidth} from './types';
    import type {EditorsLocalizationFunction} from '../../../localizations/types';
    import {createValueEditorsLabelContext, setValueEditorLabelContext} from '../../label/value-editor-label-context';
    import ValueEditorLabel from '../../label/ValueEditorLabel.svelte';
    import type {FormControlOptions} from '../../forms/types';
    import {getInjector} from 'core/svelte-context/context';

    type MODEL_VALUE = $$Generic;
    type OBJECT_VALUE = $$Generic;

    export let model: MODEL_VALUE = null; // set default null because if forms model is empty object, it will throw uninitialized props warnings

    export let field: ObjectValueEditorField<OBJECT_VALUE>;
    export let labelsWidth: ObjectValueEditorLabelsWidth;
    export let formControlOptions: FormControlOptions;
    export let forceShowErrors: boolean;


    const localize: EditorsLocalizationFunction<ObjectValueEditorLocalizations> = getContext(EDITORS_LOCALIZE);
    const configuration = getInjector().getByToken<KpValueEditorConfigurationService>(KpValueEditorConfigurationService.serviceName);
    const debugMode = configuration.debugMode;
    const inputsWidth = 12 - (field.label ? labelsWidth : 0) - (debugMode ? 1 : 0);
    let showDebugInfo = false;
    let editorController: {getFormController: () => FormController<MODEL_VALUE>};
    let formField$: Observable<Field<MODEL_VALUE>>;
    let isTouched$: Observable<boolean>;
    let modelValue$: Observable<MODEL_VALUE>;

    onMount(() => {
        const formControl = getFormController();
        formField$ = formControl.getFieldState$();
        isTouched$ = formControl.isTouched$();
        modelValue$ = formControl.getModelValue$();
    })

    if (!field.label) {
        // if there is no label, reset context to prevent nested editors reading label for this object editor
        setValueEditorLabelContext(null);
    } else {
        setValueEditorLabelContext(createValueEditorsLabelContext(field.editor.editorId, field.label));
    }

    function showValidationError(fieldState: Field<MODEL_VALUE>, show: boolean): boolean {
        if (fieldState) {
            return fieldState.invalid && show;
        }
        return false;
    }

    export function getFormController(): FormController<MODEL_VALUE> {
        return editorController.getFormController();
    }
</script>

<template>
    <div class="form-group value-editor-form-group {field.editor.editorName} size-{field.editor.size}"

         class:hidden={!field.editor.isVisible}
         class:has-error={showValidationError($formField$, $isTouched$ || forceShowErrors)}
         class:has-not-error={!showValidationError($formField$, $isTouched$ || forceShowErrors)}>

        {#if !!field.label}
            <span class="control-label col-sm-{labelsWidth} value-editor-label">
                <ValueEditorLabel fieldRequired={field.editor.validations?.required}>
                    {field.label}
                </ValueEditorLabel>
                {#if field.hint}
                    <!-- svelte-ignore a11y-no-noninteractive-tabindex hint must be focusable in order to tooltip to appear (it is interactive) -->
                    <span class="glyphicon glyphicon-question-sign text-primary editor-label-hint"
                          tabindex="0"
                          aria-label={localize('hint')}
                          use:tooltip={{content: field.hint, role: 'note', enabled: true}}>
                    </span>
                {/if}
            </span>
        {/if}
        <div class="col-sm-{inputsWidth}">
            <KpValueEditor
                    bind:this={editorController}
                    bind:model
                    {...field.editor}
                    {formControlOptions}
                    {forceShowErrors}

                    on:form-submit-request
                    on:form-structure-change/>

            {#if debugMode && !!formField$}
                <pre class="debug-info" class:hidden={!showDebugInfo}>{JSON.stringify({
                    ...$formField$,
                    touched: $isTouched$,
                    modelValue: $modelValue$,
                    editor: field.editor
                }, null, 2)}</pre>
            {/if}
        </div>
        {#if debugMode}
            <div class="col-sm-1">
                <button class="btn btn-default btn-{field.editor.size} debug-info-btn"
                        type="button"
                        title="Show / hide debug info"
                        aria-label="Show / hide debug info"
                        on:click={() => showDebugInfo = !showDebugInfo}>
                    <span class="glyphicon glyphicon-cog" aria-hidden="true"></span>
                </button>
            </div>
        {/if}
    </div>
</template>

<style lang="less">
    .size-sm, .size-xs {
        .value-editor-label {
            font-size: 12px;
            line-height: 1.5;
        }
    }

    .size-sm {
        &.value-editor-form-group {
            margin-bottom: 10px;
        }

        .value-editor-label {
            padding-top: 5px;
        }
    }

    .size-xs {
        &.value-editor-form-group {
            margin-bottom: 5px;
        }

        .value-editor-label {
            padding-top: 1px;
        }
    }
</style>